import { Box, Button, Stack, Paper, Typography } from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ImageIcon from "@mui/icons-material/Image";
import React, { useEffect, useRef, useState } from "react";
import { CameraAltRounded } from "@mui/icons-material";
import { grey } from "@mui/material/colors";

const maxWidth = 500;
export default function CameraCapture({
	onCapture,
	compact = false,
	existingSrc = null,
	previewWidth,
	previewHeight,
}) {
	const previewMaxWidth = previewWidth ?? (compact ? 220 : maxWidth);
	const placeholderHeight = previewHeight ?? (compact ? 330 : 300);
	const videoRef = useRef(null);
	const canvasRef = useRef(null);

	const [stream, setStream] = useState(null);
	const [capturedImg, setCapturedImg] = useState(null);

	useEffect(() => {
		return () => {
			if (stream) {
				stream.getTracks().forEach((t) => t.stop());
			}
		};
	}, [stream]);

	const stopStream = () => {
		if (stream) {
			stream.getTracks().forEach((t) => t.stop());
			setStream(null);
		}
	};

	const openCamera = async () => {
		setCapturedImg(null);
		try {
			const s = await navigator.mediaDevices.getUserMedia({
				video: { facingMode: "environment" },
				audio: false,
			});
			setStream(s);
			videoRef.current.srcObject = s;
			await videoRef.current.play();
		} catch (err) {
			if (err.name === "NotAllowedError") {
				alert("Camera access denied.");
			}

			console.error("Camera Error:", err);
		}
	};

	const capture = () => {
		const video = videoRef.current;
		const canvas = canvasRef.current;
		const ctx = canvas.getContext("2d");

		canvas.width = video.videoWidth;
		canvas.height = video.videoHeight;

		ctx.drawImage(video, 0, 0);

		const dataURL = canvas.toDataURL("image/jpeg", 0.9);
		setCapturedImg(dataURL);

		stopStream();

		fetch(dataURL)
			.then((res) => res.arrayBuffer())
			.then((buf) => {
				const file = new File([buf], "camera.jpg", { type: "image/jpeg" });
				onCapture(file);
			});
	};

	const recapture = () => {
		setCapturedImg(null);
	};

	const hasPreview = Boolean(stream || capturedImg || existingSrc);
	const actionSx = {
		textTransform: "none",
		borderRadius: 1.5,
		fontWeight: 600,
		px: 2.5,
	};

	return (
		<Box
			sx={{
				display: "flex",
				width: "100%",
				alignItems: compact ? "center" : "flex-start",
				gap: 2,
			}}>
			<Paper
				elevation={compact ? 0 : 2}
				sx={{
					width: previewMaxWidth,
					height: placeholderHeight,
					maxWidth: "100%",
					borderRadius: compact ? "12px" : 2,
					overflow: "hidden",
					position: "relative",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					bgcolor: compact ? grey[50] : "#f5f5f5",
					flexShrink: 0,
					border: compact
						? hasPreview
							? `1px solid ${grey[200]}`
							: `1.5px dashed ${grey[300]}`
						: "none",
					boxShadow: compact ? "none" : undefined,
				}}>
				{!stream && !capturedImg && existingSrc && (
					<img
						src={existingSrc}
						alt="Current GRN"
						style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
					/>
				)}
				{!stream && !capturedImg && !existingSrc && (
					<Box textAlign="center" color="#777" px={2}>
						<ImageIcon sx={{ fontSize: compact ? 40 : 60, mb: compact ? 0.5 : 1, color: "#9e9e9e" }} />
						<Typography variant={compact ? "body2" : "body1"} color="text.secondary">
							No image captured
						</Typography>
					</Box>
				)}
				<video
					ref={videoRef}
					style={{
						display: stream && !capturedImg ? "block" : "none",
						width: "100%",
						height: "100%",
						objectFit: "cover",
						background: "#000",
						position: "absolute",
						inset: 0,
					}}
				/>
				{capturedImg && (
					<img
						src={capturedImg}
						alt="Captured"
						style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
					/>
				)}
			</Paper>

			<Stack direction="column" spacing={1.25} sx={{ minWidth: compact ? 160 : 140, justifyContent: "center" }}>
				{!stream && !capturedImg && (
					<Button
						variant={compact ? "outlined" : "contained"}
						startIcon={<PhotoCameraIcon />}
						onClick={openCamera}
						fullWidth
						sx={compact ? actionSx : undefined}>
						Open camera
					</Button>
				)}

				{stream && !capturedImg && (
					<Button
						variant="contained"
						disableElevation={compact}
						startIcon={<CameraAltRounded />}
						onClick={capture}
						fullWidth
						sx={compact ? actionSx : undefined}>
						Capture
					</Button>
				)}

				{capturedImg && (
					<Button
						variant="outlined"
						color="warning"
						startIcon={<RestartAltIcon />}
						onClick={recapture}
						fullWidth
						sx={compact ? actionSx : undefined}>
						Retake
					</Button>
				)}
			</Stack>

			<canvas ref={canvasRef} style={{ display: "none" }} />
		</Box>
	);
}
